import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useBusinessStore, type Agent, type VoiceInfo } from '../stores/business';

const VOICE_PROVIDERS = [
  { id: 'openai', name: 'OpenAI', description: 'OpenAI TTS voices' },
  { id: 'elevenlabs', name: 'ElevenLabs', description: 'High-quality AI voices' },
  { id: 'google', name: 'Google TTS', description: 'Google Cloud voices' },
] as const;

const LLM_PROVIDERS = [
  { id: 'openai', name: 'OpenAI', description: 'OpenAI models (GPT-5.2, GPT-4o)' },
  { id: 'anthropic', name: 'Anthropic', description: 'Claude models (Claude 3.5 Sonnet, Claude 3 Opus)' },
] as const;

const OPENAI_MODELS = [
  { id: 'gpt-5.2', name: 'GPT-5.2 (Latest)' },
  { id: 'gpt-5-mini', name: 'GPT-5 Mini (Fast & Efficient)' },
  { id: 'gpt-5-nano', name: 'GPT-5 Nano (Fastest)' },
  { id: 'gpt-4o', name: 'GPT-4o' },
  { id: 'gpt-4o-mini', name: 'GPT-4o Mini' },
  { id: 'gpt-4-turbo', name: 'GPT-4 Turbo' },
];

const ANTHROPIC_MODELS = [
  { id: 'claude-3-5-sonnet-20241022', name: 'Claude 3.5 Sonnet (Recommended)' },
  { id: 'claude-3-opus-20240229', name: 'Claude 3 Opus' },
  { id: 'claude-3-sonnet-20240229', name: 'Claude 3 Sonnet' },
  { id: 'claude-3-haiku-20240307', name: 'Claude 3 Haiku (Fast)' },
];

const DEFAULT_SYSTEM_PROMPT = `You are a helpful AI assistant for a real estate company. Your role is to:

1. Greet callers warmly and professionally
2. Understand their real estate needs (buying, selling, renting)
3. Collect basic qualification information (budget, timeline, location preferences)
4. Answer common questions about the process
5. Schedule appointments with human agents when appropriate

Be friendly, professional, and helpful. If you don't know something, say so and offer to have a human agent follow up.`;

export default function AgentsPage() {
  const { businessId } = useParams<{ businessId: string }>();
  const navigate = useNavigate();

  const {
    businesses,
    agents,
    voices,
    isLoadingAgents,
    isLoadingVoices,
    error,
    fetchBusinesses,
    fetchAgents,
    fetchVoices,
    createAgent,
    updateAgent,
    deleteAgent,
    setCurrentAgent,
    clearError,
  } = useBusinessStore();

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingAgent, setEditingAgent] = useState<Agent | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    description: '',
    voiceProvider: 'openai' as 'openai' | 'elevenlabs' | 'google',
    voiceId: '',
    systemPrompt: DEFAULT_SYSTEM_PROMPT,
    greetingMode: 'predefined' as 'none' | 'predefined' | 'generated',
    startingMessage: '',
    llmProvider: 'openai' as 'openai' | 'anthropic',
    model: 'gpt-4o',
    temperature: 0.7,
    isDefault: false,
    calendarId: '',
    timezone: 'America/Los_Angeles',
    // SIP/Phone Configuration
    outboundPhoneNumber: '',
    sipTrunkHost: '',
    sipAuthUsername: '',
    sipAuthPassword: '',
    inboundPhoneNumber: '',
    inboundSipUri: '',
  });
  const [isSaving, setIsSaving] = useState(false);
  const [showSipPassword, setShowSipPassword] = useState(false);

  const business = businesses.find((b) => b.id === businessId);
  const rawVoices = voices[formData.voiceProvider];
  const currentVoices = Array.isArray(rawVoices) ? rawVoices : [];

  useEffect(() => {
    if (businesses.length === 0) {
      fetchBusinesses();
    }
  }, [businesses.length, fetchBusinesses]);

  useEffect(() => {
    if (businessId) {
      fetchAgents(businessId);
    }
  }, [businessId, fetchAgents]);

  useEffect(() => {
    if (formData.voiceProvider) {
      fetchVoices(formData.voiceProvider, businessId);
    }
  }, [formData.voiceProvider, businessId, fetchVoices]);

  const resetForm = () => {
    setFormData({
      name: '',
      slug: '',
      description: '',
      voiceProvider: 'openai',
      voiceId: '',
      systemPrompt: DEFAULT_SYSTEM_PROMPT,
      greetingMode: 'predefined',
      startingMessage: '',
      llmProvider: 'openai',
      model: 'gpt-4o',
      temperature: 0.7,
      isDefault: false,
      calendarId: '',
      timezone: 'America/Los_Angeles',
      outboundPhoneNumber: '',
      sipTrunkHost: '',
      sipAuthUsername: '',
      sipAuthPassword: '',
      inboundPhoneNumber: '',
      inboundSipUri: '',
    });
  };

  const openEditModal = (agent: Agent) => {
    setEditingAgent(agent);
    setFormData({
      name: agent.name,
      slug: agent.slug,
      description: agent.description || '',
      voiceProvider: agent.voiceProvider,
      voiceId: agent.voiceId || '',
      systemPrompt: agent.systemPrompt,
      greetingMode: agent.greetingMode || 'predefined',
      startingMessage: agent.startingMessage || '',
      llmProvider: agent.llmProvider || 'openai',
      model: agent.model || 'gpt-4o',
      temperature: agent.temperature || 0.7,
      isDefault: agent.isDefault,
      calendarId: agent.calendarId || '',
      timezone: agent.timezone || 'America/Los_Angeles',
      outboundPhoneNumber: agent.outboundPhoneNumber || '',
      sipTrunkHost: agent.sipTrunkHost || '',
      sipAuthUsername: agent.sipAuthUsername || '',
      sipAuthPassword: agent.sipAuthPassword || '',
      inboundPhoneNumber: agent.inboundPhoneNumber || '',
      inboundSipUri: agent.inboundSipUri || '',
    });
    setShowCreateModal(true);
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!businessId) return;

    setIsSaving(true);
    try {
      if (editingAgent) {
        await updateAgent(editingAgent.id, {
          name: formData.name,
          description: formData.description || undefined,
          voiceProvider: formData.voiceProvider,
          voiceId: formData.voiceId || undefined,
          systemPrompt: formData.systemPrompt,
          greetingMode: formData.greetingMode,
          startingMessage: formData.startingMessage || undefined,
          llmProvider: formData.llmProvider,
          model: formData.model,
          temperature: formData.temperature,
          isDefault: formData.isDefault,
          calendarId: formData.calendarId || undefined,
          timezone: formData.timezone || undefined,
          outboundPhoneNumber: formData.outboundPhoneNumber || undefined,
          sipTrunkHost: formData.sipTrunkHost || undefined,
          sipAuthUsername: formData.sipAuthUsername || undefined,
          sipAuthPassword: formData.sipAuthPassword || undefined,
          inboundPhoneNumber: formData.inboundPhoneNumber || undefined,
          inboundSipUri: formData.inboundSipUri || undefined,
        });
      } else {
        await createAgent(businessId, {
          name: formData.name,
          slug: formData.slug,
          description: formData.description || undefined,
          voiceProvider: formData.voiceProvider,
          voiceId: formData.voiceId || undefined,
          systemPrompt: formData.systemPrompt,
          greetingMode: formData.greetingMode,
          startingMessage: formData.startingMessage || undefined,
          llmProvider: formData.llmProvider,
          model: formData.model,
          temperature: formData.temperature,
          isDefault: formData.isDefault,
          calendarId: formData.calendarId || undefined,
          timezone: formData.timezone || undefined,
          outboundPhoneNumber: formData.outboundPhoneNumber || undefined,
          sipTrunkHost: formData.sipTrunkHost || undefined,
          sipAuthUsername: formData.sipAuthUsername || undefined,
          sipAuthPassword: formData.sipAuthPassword || undefined,
          inboundPhoneNumber: formData.inboundPhoneNumber || undefined,
          inboundSipUri: formData.inboundSipUri || undefined,
        });
      }
      setShowCreateModal(false);
      setEditingAgent(null);
      resetForm();
    } catch {
      // Error handled by store
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (id: string, name: string) => {
    if (confirm(`Are you sure you want to delete "${name}"?`)) {
      await deleteAgent(id);
    }
  };

  const handleSelectAgent = (agent: Agent) => {
    setCurrentAgent(agent.id);
    navigate('/');
  };

  const generateSlug = (name: string) => {
    return name
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '');
  };

  if (!business) {
    return (
      <div className="min-h-screen bg-surface-950 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-white mb-2">Business not found</h2>
          <Link to="/businesses" className="btn-primary">
            Go to Businesses
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-surface-950 p-6">
      <div className="max-w-6xl mx-auto">
        {/* Breadcrumb */}
        <nav className="flex items-center gap-2 text-sm text-surface-400 mb-6">
          <Link to="/businesses" className="hover:text-white transition-colors">
            Businesses
          </Link>
          <span>/</span>
          <span className="text-white">{business.name}</span>
          <span>/</span>
          <span className="text-primary-400">Agents</span>
        </nav>

        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-display text-3xl font-bold text-white">Agents</h1>
            <p className="text-surface-400 mt-1">
              Configure AI voice agents for {business.name}
            </p>
          </div>
          <button
            onClick={() => {
              resetForm();
              setEditingAgent(null);
              setShowCreateModal(true);
            }}
            className="btn-primary flex items-center gap-2"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            New Agent
          </button>
        </div>

        {/* Error Alert */}
        {error && (
          <div className="mb-6 rounded-lg bg-red-500/10 border border-red-500/20 p-4 flex items-center justify-between">
            <span className="text-red-400">{error}</span>
            <button onClick={clearError} className="text-red-400 hover:text-red-300">
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        )}

        {/* Loading State */}
        {isLoadingAgents && (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
          </div>
        )}

        {/* Empty State */}
        {!isLoadingAgents && agents.length === 0 && (
          <div className="text-center py-16 bg-surface-900/50 rounded-xl border border-surface-800">
            <div className="inline-flex h-16 w-16 items-center justify-center rounded-full bg-surface-800 mb-4">
              <svg className="w-8 h-8 text-surface-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
              </svg>
            </div>
            <h3 className="text-xl font-semibold text-white mb-2">No agents yet</h3>
            <p className="text-surface-400 mb-6">
              Create your first AI agent to start handling voice conversations.
            </p>
            <button
              onClick={() => {
                resetForm();
                setShowCreateModal(true);
              }}
              className="btn-primary"
            >
              Create Your First Agent
            </button>
          </div>
        )}

        {/* Agents Grid */}
        {!isLoadingAgents && agents.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {agents.map((agent) => (
              <div
                key={agent.id}
                className="card hover:border-primary-500/50 transition-colors cursor-pointer group"
                onClick={() => handleSelectAgent(agent)}
              >
                <div className="card-body">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className="h-12 w-12 rounded-lg bg-gradient-to-br from-secondary-500 to-secondary-700 flex items-center justify-center">
                        <svg className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                        </svg>
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <h3 className="font-semibold text-white group-hover:text-primary-400 transition-colors">
                            {agent.name}
                          </h3>
                          {agent.isDefault && (
                            <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-primary-500/20 text-primary-400">
                              Default
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-surface-500">/{agent.slug}</p>
                        <div className="flex items-center gap-1 mt-1">
                          <code className="text-xs text-surface-500 font-mono bg-surface-800 px-1.5 py-0.5 rounded">
                            {agent.id}
                          </code>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              navigator.clipboard.writeText(agent.id);
                              // Show a brief tooltip or toast
                              const btn = e.currentTarget;
                              btn.classList.add('text-green-400');
                              setTimeout(() => btn.classList.remove('text-green-400'), 1000);
                            }}
                            className="text-surface-500 hover:text-primary-400 transition-colors p-0.5"
                            title="Copy Agent ID"
                          >
                            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                            </svg>
                          </button>
                        </div>
                      </div>
                    </div>
                    <span
                      className={`px-2 py-1 rounded-full text-xs font-medium ${
                        agent.isActive
                          ? 'bg-green-500/10 text-green-400'
                          : 'bg-surface-700 text-surface-400'
                      }`}
                    >
                      {agent.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </div>

                  {agent.description && (
                    <p className="text-sm text-surface-400 mb-4 line-clamp-2">{agent.description}</p>
                  )}

                  <div className="space-y-2 text-sm">
                    <div className="flex items-center gap-2 text-surface-400">
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
                      </svg>
                      <span>
                        {VOICE_PROVIDERS.find((p) => p.id === agent.voiceProvider)?.name || agent.voiceProvider}
                        {agent.voiceId && ` (${agent.voiceId})`}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 text-surface-400">
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                      </svg>
                      <span>
                        {LLM_PROVIDERS.find((p) => p.id === agent.llmProvider)?.name || 'OpenAI'} - {agent.model || 'gpt-4o'}
                      </span>
                    </div>
                    {agent.startingMessage && (
                      <div className="flex items-start gap-2 text-surface-400">
                        <svg className="w-4 h-4 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
                        </svg>
                        <span className="line-clamp-1">Greeting: "{agent.startingMessage}"</span>
                      </div>
                    )}
                  </div>

                  <div className="mt-4 pt-4 border-t border-surface-800 flex items-center justify-between">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        openEditModal(agent);
                      }}
                      className="text-surface-400 hover:text-primary-400 transition-colors text-sm flex items-center gap-1"
                    >
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                      Edit
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDelete(agent.id, agent.name);
                      }}
                      className="text-surface-500 hover:text-red-400 transition-colors"
                    >
                      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Create/Edit Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="card w-full max-w-2xl my-8" onClick={(e) => e.stopPropagation()}>
            <div className="card-body max-h-[85vh] overflow-y-auto">
              <div className="flex items-center justify-between mb-6 sticky top-0 bg-surface-900 pb-4 -mt-2 pt-2">
                <h2 className="text-xl font-semibold text-white">
                  {editingAgent ? 'Edit Agent' : 'Create New Agent'}
                </h2>
                <button
                  onClick={() => {
                    setShowCreateModal(false);
                    setEditingAgent(null);
                  }}
                  className="text-surface-400 hover:text-white transition-colors"
                >
                  <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <form onSubmit={handleSave} className="space-y-6">
                {/* Agent ID (only shown when editing) */}
                {editingAgent && (
                  <div className="p-4 rounded-lg bg-surface-800/50 border border-surface-700">
                    <div className="flex items-center justify-between">
                      <div>
                        <label className="block text-sm font-medium text-surface-300 mb-1">
                          Agent ID
                        </label>
                        <p className="text-xs text-surface-500">
                          Use this ID to connect web assistants or make API calls
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={() => {
                          navigator.clipboard.writeText(editingAgent.id);
                          // Visual feedback
                          const btn = document.getElementById('copy-agent-id-btn');
                          if (btn) {
                            btn.textContent = 'Copied!';
                            btn.classList.add('bg-green-600');
                            setTimeout(() => {
                              btn.textContent = 'Copy';
                              btn.classList.remove('bg-green-600');
                            }, 2000);
                          }
                        }}
                        id="copy-agent-id-btn"
                        className="px-3 py-1.5 text-sm font-medium rounded-md bg-primary-600 hover:bg-primary-500 text-white transition-colors"
                      >
                        Copy
                      </button>
                    </div>
                    <div className="mt-2 flex items-center gap-2">
                      <code className="flex-1 text-sm font-mono text-primary-400 bg-surface-900 px-3 py-2 rounded-md select-all">
                        {editingAgent.id}
                      </code>
                    </div>
                  </div>
                )}

                {/* Basic Info */}
                <div className="space-y-4">
                  <h3 className="text-sm font-medium text-surface-300 uppercase tracking-wider">
                    Basic Information
                  </h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-surface-300 mb-2">
                        Agent Name *
                      </label>
                      <input
                        type="text"
                        value={formData.name}
                        onChange={(e) => {
                          setFormData({
                            ...formData,
                            name: e.target.value,
                            slug: editingAgent ? formData.slug : generateSlug(e.target.value),
                          });
                        }}
                        className="input"
                        placeholder="Sales Assistant"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-surface-300 mb-2">
                        URL Slug *
                      </label>
                      <input
                        type="text"
                        value={formData.slug}
                        onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                        className="input"
                        placeholder="sales-assistant"
                        pattern="[a-z0-9\-]+"
                        required
                        disabled={!!editingAgent}
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-surface-300 mb-2">
                      Description
                    </label>
                    <input
                      type="text"
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      className="input"
                      placeholder="A brief description of this agent's purpose"
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="isDefault"
                      checked={formData.isDefault}
                      onChange={(e) => setFormData({ ...formData, isDefault: e.target.checked })}
                      className="w-4 h-4 rounded border-surface-600 bg-surface-800 text-primary-500 focus:ring-primary-500"
                    />
                    <label htmlFor="isDefault" className="text-sm text-surface-300">
                      Set as default agent for this business
                    </label>
                  </div>
                </div>

                {/* Voice Configuration */}
                <div className="space-y-4 border-t border-surface-800 pt-6">
                  <h3 className="text-sm font-medium text-surface-300 uppercase tracking-wider">
                    Voice Configuration
                  </h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-surface-300 mb-2">
                        Voice Provider
                      </label>
                      <select
                        value={formData.voiceProvider}
                        onChange={(e) => {
                          const value = e.target.value as 'openai' | 'elevenlabs' | 'google';
                          console.log('Voice provider changed to:', value);
                          setFormData({
                            ...formData,
                            voiceProvider: value,
                            voiceId: '',
                          });
                        }}
                        onClick={(e) => e.stopPropagation()}
                        className="input cursor-pointer"
                      >
                        {VOICE_PROVIDERS.map((p) => (
                          <option key={p.id} value={p.id}>
                            {p.name}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-surface-300 mb-2">
                        Voice {formData.voiceProvider === 'elevenlabs' && '(Voice ID)'}
                      </label>
                      {formData.voiceProvider === 'elevenlabs' ? (
                        <input
                          type="text"
                          value={formData.voiceId}
                          onChange={(e) => setFormData({ ...formData, voiceId: e.target.value })}
                          onClick={(e) => e.stopPropagation()}
                          className="input"
                          placeholder="Enter ElevenLabs voice ID (e.g., 21m00Tcm4TlvDq8ikWAM)"
                        />
                      ) : (
                        <select
                          value={formData.voiceId}
                          onChange={(e) => setFormData({ ...formData, voiceId: e.target.value })}
                          onClick={(e) => e.stopPropagation()}
                          className="input cursor-pointer"
                          disabled={isLoadingVoices}
                        >
                          <option value="">Select a voice...</option>
                          {currentVoices.map((v: VoiceInfo) => (
                            <option key={v.id} value={v.id}>
                              {v.name} {v.gender ? `(${v.gender})` : ''}
                            </option>
                          ))}
                        </select>
                      )}
                    </div>
                  </div>
                  {formData.voiceProvider === 'elevenlabs' && (
                    <div className="p-3 rounded-lg bg-surface-800/50 border border-surface-700">
                      <p className="text-xs text-surface-400">
                        <strong className="text-surface-300">How to find your ElevenLabs Voice ID:</strong>
                        <br />
                        1. Go to <a href="https://elevenlabs.io/voice-library" target="_blank" rel="noopener noreferrer" className="text-primary-400 hover:text-primary-300 underline">ElevenLabs Voice Library</a>
                        <br />
                        2. Click on a voice to open its details
                        <br />
                        3. Copy the Voice ID from the URL or voice settings
                      </p>
                    </div>
                  )}
                </div>

                {/* Agent Behavior */}
                <div className="space-y-4 border-t border-surface-800 pt-6">
                  <h3 className="text-sm font-medium text-surface-300 uppercase tracking-wider">
                    Agent Behavior
                  </h3>
                  <div>
                    <label className="block text-sm font-medium text-surface-300 mb-2">
                      System Prompt *
                    </label>
                    <textarea
                      value={formData.systemPrompt}
                      onChange={(e) => setFormData({ ...formData, systemPrompt: e.target.value })}
                      className="input min-h-[200px] font-mono text-sm"
                      placeholder="Enter the system prompt that defines this agent's behavior..."
                      required
                    />
                    <p className="text-xs text-surface-500 mt-1">
                      This prompt defines your agent's personality, knowledge, and behavior.
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-surface-300 mb-2">
                      Greeting Mode
                    </label>
                    <div className="grid grid-cols-3 gap-3">
                      <button
                        type="button"
                        onClick={() => setFormData({ ...formData, greetingMode: 'none' })}
                        className={`p-3 rounded-lg border-2 transition-all text-left ${
                          formData.greetingMode === 'none'
                            ? 'border-primary-500 bg-primary-500/10'
                            : 'border-surface-700 hover:border-surface-600'
                        }`}
                      >
                        <div className="font-medium text-sm">Wait for User</div>
                        <div className="text-xs text-surface-500 mt-1">Agent waits silently</div>
                      </button>
                      <button
                        type="button"
                        onClick={() => setFormData({ ...formData, greetingMode: 'predefined' })}
                        className={`p-3 rounded-lg border-2 transition-all text-left ${
                          formData.greetingMode === 'predefined'
                            ? 'border-primary-500 bg-primary-500/10'
                            : 'border-surface-700 hover:border-surface-600'
                        }`}
                      >
                        <div className="font-medium text-sm">Predefined</div>
                        <div className="text-xs text-surface-500 mt-1">Use custom message</div>
                      </button>
                      <button
                        type="button"
                        onClick={() => setFormData({ ...formData, greetingMode: 'generated' })}
                        className={`p-3 rounded-lg border-2 transition-all text-left ${
                          formData.greetingMode === 'generated'
                            ? 'border-primary-500 bg-primary-500/10'
                            : 'border-surface-700 hover:border-surface-600'
                        }`}
                      >
                        <div className="font-medium text-sm">AI Generated</div>
                        <div className="text-xs text-surface-500 mt-1">LLM creates greeting</div>
                      </button>
                    </div>
                    <p className="text-xs text-surface-500 mt-2">
                      Choose how the agent initiates the conversation.
                    </p>
                  </div>
                  {formData.greetingMode === 'predefined' && (
                    <div>
                      <label className="block text-sm font-medium text-surface-300 mb-2">
                        Starting Message
                      </label>
                      <textarea
                        value={formData.startingMessage}
                        onChange={(e) => setFormData({ ...formData, startingMessage: e.target.value })}
                        className="input min-h-[80px]"
                        placeholder="Hello! Thank you for calling. How can I help you today?"
                      />
                      <p className="text-xs text-surface-500 mt-1">
                        The agent will speak this exact message when the conversation starts.
                      </p>
                    </div>
                  )}
                  {formData.greetingMode === 'generated' && (
                    <div className="p-3 rounded-lg bg-surface-800/50 border border-surface-700">
                      <p className="text-sm text-surface-400">
                        <span className="text-primary-400 font-medium">AI Generated:</span> The agent will use the LLM to generate a contextual greeting based on the system prompt and any available caller information.
                      </p>
                    </div>
                  )}
                </div>

                {/* LLM Configuration */}
                <div className="space-y-4 border-t border-surface-800 pt-6">
                  <h3 className="text-sm font-medium text-surface-300 uppercase tracking-wider">
                    LLM Configuration
                  </h3>
                  <div>
                    <label className="block text-sm font-medium text-surface-300 mb-2">
                      LLM Provider
                    </label>
                    <div className="grid grid-cols-2 gap-3">
                      {LLM_PROVIDERS.map((provider) => (
                        <button
                          key={provider.id}
                          type="button"
                          onClick={() => {
                            const defaultModel = provider.id === 'anthropic' 
                              ? 'claude-3-5-sonnet-20241022' 
                              : 'gpt-4o';
                            setFormData({ 
                              ...formData, 
                              llmProvider: provider.id as 'openai' | 'anthropic',
                              model: defaultModel,
                            });
                          }}
                          className={`p-3 rounded-lg border text-left transition-all ${
                            formData.llmProvider === provider.id
                              ? 'border-primary-500 bg-primary-500/10'
                              : 'border-surface-700 hover:border-surface-600'
                          }`}
                        >
                          <div className="font-medium text-white text-sm">{provider.name}</div>
                          <div className="text-xs text-surface-400 mt-1">{provider.description}</div>
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-surface-300 mb-2">
                        Model
                      </label>
                      <select
                        value={formData.model}
                        onChange={(e) => setFormData({ ...formData, model: e.target.value })}
                        className="input"
                      >
                        {(formData.llmProvider === 'anthropic' ? ANTHROPIC_MODELS : OPENAI_MODELS).map((m) => (
                          <option key={m.id} value={m.id}>
                            {m.name}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-surface-300 mb-2">
                        Temperature: {formData.temperature}
                      </label>
                      <input
                        type="range"
                        min="0"
                        max="1"
                        step="0.1"
                        value={formData.temperature}
                        onChange={(e) =>
                          setFormData({ ...formData, temperature: parseFloat(e.target.value) })
                        }
                        className="w-full h-2 bg-surface-700 rounded-lg appearance-none cursor-pointer"
                      />
                      <div className="flex justify-between text-xs text-surface-500 mt-1">
                        <span>Focused</span>
                        <span>Creative</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Calendar Configuration */}
                <div className="space-y-4 border-t border-surface-800 pt-6">
                  <h3 className="text-sm font-medium text-surface-300 uppercase tracking-wider">
                    Appointment Scheduling
                  </h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-surface-300 mb-2">
                        GHL Calendar ID
                      </label>
                      <input
                        type="text"
                        value={formData.calendarId}
                        onChange={(e) => setFormData({ ...formData, calendarId: e.target.value })}
                        className="input"
                        placeholder="Enter your GoHighLevel Calendar ID"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-surface-300 mb-2">
                        Timezone
                      </label>
                      <select
                        value={formData.timezone}
                        onChange={(e) => setFormData({ ...formData, timezone: e.target.value })}
                        className="input"
                      >
                        <option value="America/Los_Angeles">Pacific Time (PT)</option>
                        <option value="America/Denver">Mountain Time (MT)</option>
                        <option value="America/Chicago">Central Time (CT)</option>
                        <option value="America/New_York">Eastern Time (ET)</option>
                        <option value="America/Phoenix">Arizona (MST)</option>
                        <option value="Pacific/Honolulu">Hawaii (HST)</option>
                        <option value="America/Anchorage">Alaska (AKST)</option>
                        <option value="UTC">UTC</option>
                      </select>
                    </div>
                  </div>
                  <p className="text-xs text-surface-500">
                    If provided, the agent can check availability and book appointments.
                  </p>
                  <div className="p-3 rounded-lg bg-surface-800/50 border border-surface-700">
                    <p className="text-xs text-surface-400">
                      <strong className="text-surface-300">How to find your GHL Calendar ID:</strong>
                      <br />
                      1. Go to your GoHighLevel account → <strong>Calendars</strong>
                      <br />
                      2. Click on the calendar you want to use
                      <br />
                      3. Copy the Calendar ID from the URL or calendar settings
                    </p>
                  </div>
                </div>

                {/* Phone/SIP Configuration */}
                <div className="space-y-4 border-t border-surface-800 pt-6">
                  <h3 className="text-sm font-medium text-surface-300 uppercase tracking-wider">
                    📞 Phone Configuration
                  </h3>
                  
                  {/* Outbound Calling */}
                  <div className="space-y-4">
                    <h4 className="text-sm font-medium text-surface-400">Outbound Calling (GHL SIP Trunk)</h4>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-surface-300 mb-2">
                          Outbound Phone Number
                        </label>
                        <input
                          type="text"
                          value={formData.outboundPhoneNumber}
                          onChange={(e) => setFormData({ ...formData, outboundPhoneNumber: e.target.value })}
                          className="input"
                          placeholder="+18551234567"
                        />
                        <p className="text-xs text-surface-500 mt-1">
                          Caller ID for outbound calls
                        </p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-surface-300 mb-2">
                          SIP Trunk Host
                        </label>
                        <input
                          type="text"
                          value={formData.sipTrunkHost}
                          onChange={(e) => setFormData({ ...formData, sipTrunkHost: e.target.value })}
                          className="input"
                          placeholder="ratetrac.sip.ashburn.twilio.com"
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-surface-300 mb-2">
                          SIP Auth Username
                        </label>
                        <input
                          type="text"
                          value={formData.sipAuthUsername}
                          onChange={(e) => setFormData({ ...formData, sipAuthUsername: e.target.value })}
                          className="input"
                          placeholder="855"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-surface-300 mb-2">
                          SIP Auth Password
                        </label>
                        <div className="relative">
                          <input
                            type={showSipPassword ? 'text' : 'password'}
                            value={formData.sipAuthPassword}
                            onChange={(e) => setFormData({ ...formData, sipAuthPassword: e.target.value })}
                            className="input pr-10"
                            placeholder="••••••••"
                          />
                          <button
                            type="button"
                            onClick={() => setShowSipPassword(!showSipPassword)}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-surface-400 hover:text-white"
                          >
                            {showSipPassword ? (
                              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                              </svg>
                            ) : (
                              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                              </svg>
                            )}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Inbound Calling */}
                  <div className="space-y-4 mt-6">
                    <h4 className="text-sm font-medium text-surface-400">Inbound Calling (Twilio → LiveKit)</h4>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-surface-300 mb-2">
                          Inbound Phone Number
                        </label>
                        <input
                          type="text"
                          value={formData.inboundPhoneNumber}
                          onChange={(e) => setFormData({ ...formData, inboundPhoneNumber: e.target.value })}
                          className="input"
                          placeholder="+18001234567"
                        />
                        <p className="text-xs text-surface-500 mt-1">
                          Twilio number that forwards to the agent
                        </p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-surface-300 mb-2">
                          Inbound SIP URI
                        </label>
                        <input
                          type="text"
                          value={formData.inboundSipUri}
                          onChange={(e) => setFormData({ ...formData, inboundSipUri: e.target.value })}
                          className="input"
                          placeholder="sip:agent@sip.optimat.io"
                        />
                        <p className="text-xs text-surface-500 mt-1">
                          SIP address for LiveKit to receive calls
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="p-3 rounded-lg bg-surface-800/50 border border-surface-700">
                    <p className="text-xs text-surface-400">
                      <strong className="text-surface-300">Phone Setup Overview:</strong>
                      <br />
                      <strong>Outbound:</strong> Uses GHL's SIP trunk to make calls with your LC phone numbers
                      <br />
                      <strong>Inbound:</strong> Twilio number forwards calls via SIP to your LiveKit agent
                    </p>
                  </div>

                  {editingAgent && (formData.sipTrunkHost || formData.inboundPhoneNumber) && (
                    <button
                      type="button"
                      onClick={async () => {
                        if (!editingAgent) return;
                        try {
                          // Get token from Zustand persisted state
                          const authState = localStorage.getItem('mia-auth');
                          const token = authState ? JSON.parse(authState).state?.token : null;
                          
                          if (!token) {
                            alert('Please log in again to setup SIP trunks.');
                            return;
                          }
                          
                          const response = await fetch(
                            `${import.meta.env.VITE_API_BASE_URL || '/api'}/sip/agents/${editingAgent.id}/setup`,
                            {
                              method: 'POST',
                              headers: {
                                'Content-Type': 'application/json',
                                'Authorization': `Bearer ${token}`,
                              },
                            }
                          );
                          const result = await response.json();
                          if (result.success) {
                            alert(`SIP trunks configured successfully!\n\nOutbound: ${result.data.outboundTrunk ? '✓' : '—'}\nInbound: ${result.data.inboundTrunk ? '✓' : '—'}\nDispatch: ${result.data.dispatchRule ? '✓' : '—'}${result.data.errors?.length ? '\n\nWarnings:\n' + result.data.errors.join('\n') : ''}`);
                          } else {
                            alert(`Failed to setup SIP: ${result.error?.message || 'Unknown error'}`);
                          }
                        } catch (err) {
                          alert(`Error setting up SIP: ${err instanceof Error ? err.message : 'Unknown error'}`);
                        }
                      }}
                      className="w-full py-2 px-4 rounded-lg bg-green-600 hover:bg-green-500 text-white font-medium transition-colors flex items-center justify-center gap-2"
                    >
                      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                      </svg>
                      Setup SIP Trunks on LiveKit Server
                    </button>
                  )}
                </div>

                <div className="flex gap-3 pt-4 border-t border-surface-800">
                  <button
                    type="button"
                    onClick={() => {
                      setShowCreateModal(false);
                      setEditingAgent(null);
                    }}
                    className="btn-secondary flex-1"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isSaving}
                    className="btn-primary flex-1"
                  >
                    {isSaving ? 'Saving...' : editingAgent ? 'Save Changes' : 'Create Agent'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

